import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError } from '@/api/client'
import { DataTable, type Column } from '@/components/DataTable'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Modal } from '@/components/ui/Modal'
import { listUsers } from '@/features/auth/api'
import { useAuth } from '@/features/auth/store'
import {
  deleteRule,
  describeRate,
  formatMoney,
  listRules,
  type CommissionRule,
} from '@/features/commissions/api'
import { RuleFormDialog } from '@/features/commissions/RuleFormDialog'
import { flattenCategories, listCategories } from '@/features/inventory/api'
import { toast } from '@/lib/toast'

/**
 * The rate cards themselves (H4), separate from the statement and the payroll preview: this
 * is where a rule is written down, not where its effect is read back.
 *
 * A rule's `user_id` and `category_id` are ids only -- `RuleRead` has no names to carry,
 * unlike an accrual, which is always shown next to the invoice and rep it came from. So
 * this screen resolves both against the same lists the form's pickers use, rather than
 * showing a raw UUID in a table meant to be read at a glance.
 */

function DeleteRuleDialog({
  rule,
  onClose,
}: {
  rule: CommissionRule | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const remove = useMutation({
    mutationFn: (id: string) => deleteRule(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['commission-rules'] })
      toast.success(`Deleted ${rule?.name}.`)
      onClose()
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'Could not reach the server.')
    },
  })

  return (
    <Modal
      open={rule !== null}
      onClose={onClose}
      title={`Delete ${rule?.name ?? 'this rule'}?`}
      description="Accruals already recorded under this rule keep their frozen figures -- deleting it only stops it applying to anything new."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={remove.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={remove.isPending}
            onClick={() => rule && remove.mutate(rule.id)}
          >
            Delete rule
          </Button>
        </>
      }
    >
      <p className="text-sm text-slate-600 dark:text-slate-300">This cannot be undone.</p>
    </Modal>
  )
}

export function CommissionRules() {
  const [page, setPage] = useState(1)
  const [dialog, setDialog] = useState<{ rule?: CommissionRule } | null>(null)
  const [pendingDelete, setPendingDelete] = useState<CommissionRule | null>(null)
  const can = useAuth((s) => s.can)

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['commission-rules', page],
    queryFn: () => listRules({ page }),
    retry: false,
  })

  const users = useQuery({ queryKey: ['users', 1], queryFn: () => listUsers(1), retry: false })
  const categories = useQuery({ queryKey: ['categories'], queryFn: listCategories, retry: false })
  const userNames = new Map((users.data?.data ?? []).map((user) => [user.id, user.full_name]))
  const categoryNames = new Map(
    flattenCategories(categories.data ?? []).map((category) => [category.id, category.label.trim()]),
  )

  const rules = data?.data ?? []
  const meta = data?.meta

  const columns: Column<CommissionRule>[] = [
    {
      key: 'name',
      header: 'Name',
      className: 'font-medium',
      sortValue: (rule) => rule.name,
      cell: (rule) =>
        can('commissions', 'update') ? (
          <button
            type="button"
            onClick={() => setDialog({ rule })}
            className="text-left hover:underline"
          >
            {rule.name}
          </button>
        ) : (
          rule.name
        ),
    },
    {
      key: 'rate',
      header: 'Rate',
      className: 'tabular-nums',
      cell: (rule) => describeRate(rule, formatMoney),
    },
    {
      key: 'applies_to',
      header: 'Applies to',
      className: 'text-slate-500 dark:text-slate-400',
      cell: (rule) => {
        const rep = rule.user_id ? (userNames.get(rule.user_id) ?? 'Unknown rep') : 'Every rep'
        const category = rule.category_id
          ? (categoryNames.get(rule.category_id) ?? 'Unknown category')
          : 'Every category'
        return `${rep} · ${category}`
      },
    },
    {
      key: 'valid_from',
      header: 'Valid from',
      sortValue: (rule) => rule.valid_from,
      cell: (rule) => rule.valid_from,
    },
    {
      key: 'valid_to',
      header: 'Valid to',
      className: 'text-slate-500 dark:text-slate-400',
      sortValue: (rule) => rule.valid_to ?? '',
      cell: (rule) => rule.valid_to ?? 'Open-ended',
    },
    {
      key: 'sequence',
      header: 'Sequence',
      className: 'tabular-nums text-slate-500 dark:text-slate-400',
      sortValue: (rule) => rule.sequence,
      cell: (rule) => rule.sequence,
    },
    {
      key: 'is_active',
      header: 'Status',
      sortValue: (rule) => (rule.is_active ? 0 : 1),
      cell: (rule) => (
        <span
          className={
            rule.is_active
              ? 'rounded bg-success/10 px-2 py-0.5 text-xs font-medium text-success'
              : 'rounded bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300'
          }
        >
          {rule.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    ...(can('commissions', 'delete')
      ? [
          {
            key: 'actions',
            header: '',
            headerClassName: 'sr-only',
            cell: (rule: CommissionRule) => (
              <Button variant="ghost" size="sm" onClick={() => setPendingDelete(rule)}>
                Delete
              </Button>
            ),
          } satisfies Column<CommissionRule>,
        ]
      : []),
  ]

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Commission rate cards</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {meta ? `${meta.total} rule${meta.total === 1 ? '' : 's'}` : 'Loading…'}
          </p>
        </div>
        {can('commissions', 'create') && <Button onClick={() => setDialog({})}>New rule</Button>}
      </header>

      {(users.error || categories.error) && (
        <Alert tone="info">
          Rep or category names could not be loaded, so this table falls back to showing ids
          were a name is missing.
        </Alert>
      )}

      <Card>
        <CardHeader
          title="All rules"
          description="Read from /api/v1/commissions/rules, guarded by commissions:read. The most specific rule matching a sale wins; sequence breaks a tie between two that are equally specific."
        />
        <DataTable
          rows={rules}
          columns={columns}
          getRowId={(rule) => rule.id}
          caption="Commission rate cards"
          isLoading={isLoading}
          error={error}
          emptyMessage="No commission rules yet."
          search={{
            placeholder: 'Search by name…',
            matches: (rule, query) => rule.name.toLowerCase().includes(query),
          }}
          pagination={
            meta
              ? {
                  page: meta.page,
                  totalPages: meta.total_pages,
                  onPage: setPage,
                  isFetching,
                  total: meta.total,
                }
              : undefined
          }
        />
      </Card>

      {dialog && (
        <RuleFormDialog
          key={dialog.rule?.id ?? 'new'}
          open
          rule={dialog.rule}
          onClose={() => setDialog(null)}
        />
      )}

      <DeleteRuleDialog rule={pendingDelete} onClose={() => setPendingDelete(null)} />
    </div>
  )
}
